/// <reference types="@helsenorge/framework-utils/types/hn"/>

import { useEffect, useRef, useState } from 'react';

import { sessionUpdaterEnabledToggle } from 'utils/feature-toggles';

import { type AccessTokenExpirationOperationResponse, SessionTimeoutAction } from '../../types/entities';

import Modal from '@helsenorge/designsystem-react/components/Modal';

import { trackError } from '@helsenorge/framework-utils/adobe-analytics';
import { hasLoggedInCookie } from '@helsenorge/framework-utils/cookie';
import { HelsenorgeHttpError, getTjenesterUrl } from '@helsenorge/framework-utils/hn-proxy-service';
import { get, put } from '@helsenorge/framework-utils/hn-tjeneste-service';
import { setAllowNavigation } from '@helsenorge/framework-utils/hn-user';
import { HeaderFooterEvents } from '@helsenorge/framework-utils/web-component/constants';
import { HNeventSoonAutoSignOut } from '@helsenorge/framework-utils/web-component/events';
import useWebComponentEvents from '@helsenorge/framework-utils/web-component/useWebComponentEvents';

import { TimerKeys, getCountdownTime, getSessionConfig, setTimer, stopAllTimers } from './session-utils';

interface WarningLightboxProps {
  resources?: {
    lightboxInactivityTitle?: string;
    lightboxInactivityDescription?: string;
    lightboxInactivityTimer?: string;
    lightboxInactivityDoYouWantToStay?: string;
    lightboxInactivityButtonYes?: string;
    lightboxInactivityButtonSignOut?: string;
    inactivitySignedOutTitle?: string;
    inactivitySignedOutButtonGoToRoot?: string;
    inactivitySignedOutButtonSignInAgain?: string;
  };
  authorized: boolean;
}

interface SessionContext {
  loggedInCookie: boolean;
  config: ReturnType<typeof getSessionConfig>;
  setState: React.Dispatch<
    React.SetStateAction<{
      automaticSignOutWarning: boolean;
      minutes: number;
      seconds: number;
    }>
  >;
}

const AccessTokenExpiration = '/api/v1/AccessTokenExpiration';

const getExpirationDateFromResponse = (response: AccessTokenExpirationOperationResponse): Date => {
  const timeString = response.Expire as string;
  let expirationMs: number = Date.parse(timeString);
  if (!timeString.endsWith('Z')) {
    expirationMs = Date.parse(timeString + 'Z');
  }
  const expiration: Date = new Date(expirationMs);
  return expiration;
};

const replacePlaceholder = (placeholder: string, text: string, value: string): string => {
  if (!text) {
    return '';
  }
  const expression = new RegExp(placeholder, 'g');
  return text.replace(expression, value);
};

function triggerAutomaticSignOut(loggedInCookie: boolean): void {
  stopAllTimers();
  setAllowNavigation(true);
  trackError('level4', 'Inactivity');
  if (loggedInCookie) {
    window.location.replace(`${getTjenesterUrl()}/Auth/AutoSignOut`);
  }
  if (!loggedInCookie) {
    window.location.replace(`${getTjenesterUrl()}/autosignout`);
  }
}

function handleError(error: unknown, loggedInCookie: boolean): void {
  if (error instanceof HelsenorgeHttpError) {
    triggerAutomaticSignOut(loggedInCookie);
  }
}

function setTimeLeft(expirationTime: number, setState: SessionContext['setState']): void {
  setState({
    automaticSignOutWarning: true,
    minutes: getCountdownTime(expirationTime - Date.now()).minutes,
    seconds: getCountdownTime(expirationTime - Date.now()).seconds,
  });
}

function handleExpiration(ctx: SessionContext, response?: AccessTokenExpirationOperationResponse): void {
  const expiration: Date | undefined = response && getExpirationDateFromResponse(response);
  const now = Date.now();
  const expirationTime = expiration ? expiration.getTime() : ctx.config.tokenLifetime + now;
  const warningTime = expirationTime - ctx.config.warnUserWhenRemaining - now;
  const logOutTime = expirationTime - now;
  const moreThanWarningTime = warningTime > 0;
  const moreThanLogOutTime = logOutTime > 0;
  if (!moreThanLogOutTime) {
    triggerAutomaticSignOut(ctx.loggedInCookie);
    return;
  }
  if (moreThanWarningTime) {
    ctx.setState({ automaticSignOutWarning: false, minutes: 0, seconds: 0 });
    setTimer(TimerKeys.SignOutCountdown, () => getAccessTokenExpiration(ctx), warningTime);
  }
  if (!moreThanWarningTime) {
    setTimer(TimerKeys.AutomaticSignOut, () => getAccessTokenExpiration(ctx), logOutTime);
    window[TimerKeys.SignOutCountdown] = window.setInterval(() => setTimeLeft(expirationTime, ctx.setState), 1000);
  }
}

function getAccessTokenExpiration(ctx: SessionContext): void {
  stopAllTimers();
  get<AccessTokenExpirationOperationResponse>(AccessTokenExpiration)
    .then(response => response && handleExpiration(ctx, response))
    .catch(error => handleError(error, ctx.loggedInCookie));
}

function onSignOutClick(): void {
  stopAllTimers();
  setAllowNavigation(true);
  window.location.replace(`${getTjenesterUrl()}/Auth/SignOut`);
}

function onExtendTicketClick(ctx: SessionContext): void {
  stopAllTimers();
  put<AccessTokenExpirationOperationResponse>(AccessTokenExpiration)
    .then(response => response && handleExpiration(ctx, response))
    .catch(error => handleError(error, ctx.loggedInCookie));
}

const WarningLightbox: React.FC<WarningLightboxProps> = ({ resources, authorized }) => {
  const sessionUpdaterEnabled = sessionUpdaterEnabledToggle();
  const loggedInCookie = hasLoggedInCookie();
  const ref = useRef<HTMLDivElement>(null);
  const { subscribe } = useWebComponentEvents<HTMLDivElement>('hn-webcomp-session', ref);
  const config = getSessionConfig();
  const { minutes, seconds } = getCountdownTime(config.countdown);
  const [state, setState] = useState({
    automaticSignOutWarning: false,
    minutes: minutes,
    seconds: seconds,
  });

  const ctx: SessionContext = { loggedInCookie, config, setState };

  const getTimeOutTime = (placeholder: string, text: string, value: number | undefined, appendZero = true): string => {
    let timeout = '';
    if (value === undefined) {
      return timeout;
    }

    if (value < 10 && appendZero) {
      timeout = replacePlaceholder(placeholder, text, '0' + value.toString());
    } else {
      timeout = replacePlaceholder(placeholder, text, value.toString());
    }

    return timeout;
  };

  const getTimeOutString = (): string => {
    let timeout = getTimeOutTime('{mm}', resources?.lightboxInactivityTimer ?? '', state.minutes);
    timeout = getTimeOutTime('{ss}', timeout, state.seconds);
    return timeout;
  };

  useEffect(() => {
    if (authorized) {
      handleExpiration(ctx);
    }
    const cleanup = subscribe(HeaderFooterEvents.setonshowsignoutbox, (event: CustomEvent) => {
      if (event.detail.onShowSignOutBox === SessionTimeoutAction.RequestExtension) {
        onExtendTicketClick(ctx);
      } else {
        getAccessTokenExpiration(ctx);
      }
    });
    if (authorized && sessionUpdaterEnabled) {
      const pageshowHandler = (): void => getAccessTokenExpiration(ctx);
      const visibilityHandler = (): void => {
        if (document.visibilityState === 'visible') {
          getAccessTokenExpiration(ctx);
        }
      };
      window.addEventListener('pageshow', pageshowHandler);
      document.addEventListener('visibilitychange', visibilityHandler);
      return (): void => {
        cleanup();
        window.removeEventListener('pageshow', pageshowHandler);
        document.removeEventListener('visibilitychange', visibilityHandler);
      };
    }
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (state.automaticSignOutWarning) {
      HNeventSoonAutoSignOut();
    }
  }, [state.automaticSignOutWarning]);

  if (!state.automaticSignOutWarning || !resources?.lightboxInactivityTitle) {
    return <div ref={ref} />;
  }
  return (
    <div ref={ref}>
      <Modal
        title={resources?.lightboxInactivityTitle ?? ''}
        noCloseButton
        description={`${resources?.lightboxInactivityDescription}
      ${getTimeOutString()}
      ${resources?.lightboxInactivityDoYouWantToStay}`}
        primaryButtonText={resources?.lightboxInactivityButtonYes ?? ''}
        secondaryButtonText={resources?.lightboxInactivityButtonSignOut ?? ''}
        onSuccess={() => onExtendTicketClick(ctx)}
        onClose={onSignOutClick}
      />
    </div>
  );
};

export default WarningLightbox;
